import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Tabs, Tab, ButtonGroup, ToggleButton, Badge, Modal } from 'react-bootstrap';
import { getAvisos, getMedios } from '../services/apiService';
import { format, addMonths, subMonths, parseISO, isValid, startOfMonth, endOfMonth, subDays, startOfYear, endOfYear } from 'date-fns';
import { es } from 'date-fns/locale';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';

// Registrar los componentes necesarios para Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale
);

const EvolucionClientes = () => {
  // Estado para tipo de periodo seleccionado
  const [tipoPeriodo, setTipoPeriodo] = useState('mes-actual');
  
  // Estados para filtros de periodo - Modificar para usar mes actual completo por defecto
  const [periodoActualInicio, setPeriodoActualInicio] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [periodoActualFin, setPeriodoActualFin] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  
  // Calcular automáticamente el mismo periodo pero del año anterior
  const [periodoComparacionInicio, setPeriodoComparacionInicio] = useState(
    format(subMonths(parseISO(periodoActualInicio), 12), 'yyyy-MM-dd')
  );
  const [periodoComparacionFin, setPeriodoComparacionFin] = useState(
    format(subMonths(parseISO(periodoActualFin), 12), 'yyyy-MM-dd')
  );
  
  // Estados para datos
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [datosActual, setDatosActual] = useState(null);
  const [datosComparacion, setDatosComparacion] = useState(null);
  const [evolucionClientes, setEvolucionClientes] = useState([]);
  const [clientePerdidosFiltro, setClientePerdidosFiltro] = useState('todos'); // 'todos', 'perdidos', 'nuevos', 'activos'
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [datosGrafico, setDatosGrafico] = useState(null);
  
  // Estado para filtro de medio - Inicializar con Antonio Puga (ID: 58)
  const [medioSeleccionado, setMedioSeleccionado] = useState('58');
  const [mediosDisponibles, setMediosDisponibles] = useState([]);
  
  // Nuevo estado para la búsqueda de clientes
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [clientesFiltradosBusqueda, setClientesFiltradosBusqueda] = useState([]);
  
  // Nuevo estado para tipo de comparación
  const [tipoComparacion, setTipoComparacion] = useState('anio_anterior'); // 'anio_anterior' o 'periodo_atras'
  
  // Estado para el modal
  const [showModal, setShowModal] = useState(false);
  
  // Actualizar periodo de comparación cuando cambie el periodo actual
  useEffect(() => {
    if (periodoActualInicio && isValid(parseISO(periodoActualInicio))) {
      setPeriodoComparacionInicio(
        format(subMonths(parseISO(periodoActualInicio), 12), 'yyyy-MM-dd')
      );
    }
    
    if (periodoActualFin && isValid(parseISO(periodoActualFin))) {
      setPeriodoComparacionFin(
        format(subMonths(parseISO(periodoActualFin), 12), 'yyyy-MM-dd')
      );
    }
  }, [periodoActualInicio, periodoActualFin]);
  
  // Modificar el useEffect que actualiza las fechas de comparación
  useEffect(() => {
    if (periodoActualInicio && periodoActualFin) {
      const inicio = new Date(periodoActualInicio);
      const fin = new Date(periodoActualFin);
      
      if (tipoComparacion === 'anio_anterior') {
        // Mismo período del año anterior
        const nuevaFechaInicio = new Date(inicio);
        nuevaFechaInicio.setFullYear(inicio.getFullYear() - 1);
        
        const nuevaFechaFin = new Date(fin);
        nuevaFechaFin.setFullYear(fin.getFullYear() - 1);
        
        setPeriodoComparacionInicio(nuevaFechaInicio.toISOString().split('T')[0]);
        setPeriodoComparacionFin(nuevaFechaFin.toISOString().split('T')[0]);
      } else {
        // Mismo período hacia atrás
        const diffDias = Math.ceil((fin - inicio) / (1000 * 60 * 60 * 24));
        
        const nuevaFechaFin = new Date(inicio);
        nuevaFechaFin.setDate(nuevaFechaFin.getDate() - 1);
        
        const nuevaFechaInicio = new Date(nuevaFechaFin);
        nuevaFechaInicio.setDate(nuevaFechaInicio.getDate() - diffDias);
        
        setPeriodoComparacionInicio(nuevaFechaInicio.toISOString().split('T')[0]);
        setPeriodoComparacionFin(nuevaFechaFin.toISOString().split('T')[0]);
      }
    }
  }, [periodoActualInicio, periodoActualFin, tipoComparacion]);
  
  // Cargar medios disponibles
  useEffect(() => {
    const cargarMedios = async () => {
      try {
        const response = await getMedios();
        if (response.success) {
          setMediosDisponibles(response.data);
          
          // Verificar si Antonio Puga (ID: 58) existe en los medios disponibles
          const antonioPugaExiste = response.data.some(medio => medio.id === 58 || medio.id === '58');
          
          if (!antonioPugaExiste) {
            console.warn('Medio "Antonio Puga y CIA LTDA" (ID: 58) no encontrado en los medios disponibles');
            // Si Antonio Puga no existe, establecer el primer medio disponible como predeterminado
            if (response.data.length > 0) {
              setMedioSeleccionado(response.data[0].id.toString());
            }
          }
        }
      } catch (error) {
        console.error('Error al cargar medios:', error);
      }
    };
    
    cargarMedios();
  }, []);
  
  // Cargar datos solo una vez al iniciar el componente
  useEffect(() => {
    cargarDatos();
    // No incluir dependencias para que solo se ejecute al montar el componente
  }, []);
  
  // Cargar datos de evolución de clientes
  const cargarDatos = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Crear objeto de filtros
      const filtros = {
        fechaInicio: periodoActualInicio,
        fechaFin: periodoActualFin,
        limit: 1000000  // Asegurar que se carguen todos los avisos posibles
      };
      
      // Agregar filtro de medio si está seleccionado
      if (medioSeleccionado) {
        filtros.medio = medioSeleccionado;
      }
      
      console.log('Filtrando con parámetros:', filtros);
      
      // Cargar datos del periodo actual
      console.log('Cargando datos del periodo actual...');
      const responseActual = await getAvisos(filtros);
      console.log(`Avisos cargados para el periodo actual: ${responseActual.data?.length || 0}`);
      
      // Crear objeto de filtros para comparación
      const filtrosComparacion = {
        fechaInicio: periodoComparacionInicio,
        fechaFin: periodoComparacionFin,
        limit: 1000000  // Asegurar que se carguen todos los avisos posibles
      };
      
      // Agregar filtro de medio para comparación si está seleccionado
      if (medioSeleccionado) {
        filtrosComparacion.medio = medioSeleccionado;
      }
      
      // Cargar datos del periodo de comparación
      console.log('Cargando datos del periodo de comparación...');
      const responseComparacion = await getAvisos(filtrosComparacion);
      console.log(`Avisos cargados para el periodo de comparación: ${responseComparacion.data?.length || 0}`);
      
      if (responseActual.success && responseComparacion.success) {
        setDatosActual(responseActual.data);
        setDatosComparacion(responseComparacion.data);
        procesarDatosEvolucion(responseActual.data, responseComparacion.data);
      } else {
        setError('No se pudieron cargar los datos correctamente');
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setError('Error de conexión al cargar datos. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Procesar datos para análisis de evolución
  const procesarDatosEvolucion = (datosActuales, datosAnteriores) => {
    if (!datosActuales || !datosAnteriores) return;
    
    // Agrupar datos por cliente
    const clientesActuales = {};
    const clientesAnteriores = {};
    
    // Procesar datos actuales
    datosActuales.forEach(aviso => {
      if (!aviso.empresa_cliente) return;
      
      const cliente = aviso.empresa_cliente;
      const valor = parseFloat(aviso.valor_con_descuento || 0);
      
      if (clientesActuales[cliente]) {
        clientesActuales[cliente].inversion += valor;
        clientesActuales[cliente].avisos += 1;
      } else {
        clientesActuales[cliente] = {
          nombre: cliente,
          inversion: valor,
          avisos: 1,
          detalleAvisos: []
        };
      }
      
      // Guardar detalle del aviso para el gráfico 
      clientesActuales[cliente].detalleAvisos.push({
        fecha: aviso.fecha_publicacion,
        valor: valor,
        nid: aviso.nid,
        titulo: aviso.titulo || 'Aviso sin título'
      });
    });
    
    // Procesar datos anteriores
    datosAnteriores.forEach(aviso => {
      if (!aviso.empresa_cliente) return;
      
      const cliente = aviso.empresa_cliente;
      const valor = parseFloat(aviso.valor_con_descuento || 0);
      
      if (clientesAnteriores[cliente]) {
        clientesAnteriores[cliente].inversion += valor;
        clientesAnteriores[cliente].avisos += 1;
      } else {
        clientesAnteriores[cliente] = {
          nombre: cliente,
          inversion: valor,
          avisos: 1,
          detalleAvisos: []
        };
      }
      
      // Guardar detalle del aviso para el gráfico
      clientesAnteriores[cliente].detalleAvisos.push({
        fecha: aviso.fecha_publicacion,
        valor: valor,
        nid: aviso.nid,
        titulo: aviso.titulo || 'Aviso sin título'
      });
    });
    
    // Crear lista consolidada con todos los clientes de ambos periodos
    const todosClientes = new Set([
      ...Object.keys(clientesActuales),
      ...Object.keys(clientesAnteriores)
    ]);
    
    // Calcular evolución por cliente
    const evolucion = Array.from(todosClientes).map(cliente => {
      const actual = clientesActuales[cliente] || { inversion: 0, avisos: 0, detalleAvisos: [] };
      const anterior = clientesAnteriores[cliente] || { inversion: 0, avisos: 0, detalleAvisos: [] };
      
      // Calcular variación
      const variacionInversion = actual.inversion - anterior.inversion;
      const variacionPorcentaje = anterior.inversion !== 0 
        ? (variacionInversion / anterior.inversion) * 100 
        : actual.inversion > 0 ? 100 : 0;
      
      // Determinar estado - Importante: estos valores deben coincidir EXACTAMENTE con los eventKey de las Tabs
      let estado = 'activo';
      if (anterior.inversion > 0 && actual.inversion === 0) {
        estado = 'perdidos';
      } else if (anterior.inversion === 0 && actual.inversion > 0) {
        estado = 'nuevos';
      }
      
      return {
        cliente,
        inversionActual: actual.inversion,
        inversionAnterior: anterior.inversion,
        avisosActual: actual.avisos,
        avisosAnterior: anterior.avisos,
        variacionInversion,
        variacionPorcentaje,
        estado,
        detalleActual: actual.detalleAvisos,
        detalleAnterior: anterior.detalleAvisos
      };
    });
    
    // Ordenar por variación porcentual (descendente)
    evolucion.sort((a, b) => b.variacionPorcentaje - a.variacionPorcentaje);
    
    setEvolucionClientes(evolucion);
  };
  
  // Función para formatear moneda
  const formatearMoneda = (valor) => {
    if (!valor) return '$0';
    return `$${parseInt(valor).toLocaleString('es-CL')}`;
  };
  
  // Función para formatear porcentaje
  const formatearPorcentaje = (valor) => {
    if (valor === Infinity || isNaN(valor)) return '-';
    
    return (
      <span className={valor >= 0 ? 'text-success' : 'text-danger'}>
        {valor >= 0 ? '+' : ''}{valor.toFixed(2)}%
      </span>
    );
  };
  
  // Filtrar clientes según el estado seleccionado
  const clientesFiltrados = evolucionClientes.filter(cliente => {
    if (clientePerdidosFiltro === 'todos') return true;
    return cliente.estado === clientePerdidosFiltro;
  });
  
  // Filtrar clientes por búsqueda
  useEffect(() => {
    if (busquedaCliente.trim() === '') {
      setClientesFiltradosBusqueda([]);
      return;
    }
    
    const filtrados = evolucionClientes
      .filter(cliente => 
        cliente.cliente.toLowerCase().includes(busquedaCliente.toLowerCase())
      )
      .slice(0, 10); // Limitar a 10 resultados
    
    setClientesFiltradosBusqueda(filtrados);
  }, [busquedaCliente, evolucionClientes]);

  // Manejar selección directa de cliente
  const handleSeleccionarCliente = (nombreCliente) => {
    setClienteSeleccionado(nombreCliente);
    setBusquedaCliente('');
    setClientesFiltradosBusqueda([]);
    
    // Abrir el modal al seleccionar un cliente
    if (nombreCliente) {
      setShowModal(true);
    }
  };
  
  // Función para formatear fecha de manera segura
  const formatearFechaSegura = (fecha, formatoDeseado = 'dd/MM/yyyy') => {
    try {
      // Verificar si la fecha es válida
      if (!fecha) return 'Sin fecha';
      
      let fechaObj;
      if (typeof fecha === 'string') {
        fechaObj = parseISO(fecha);
      } else if (fecha instanceof Date) {
        fechaObj = fecha;
      } else {
        return 'Formato inválido';
      }
      
      // Verificar si la fecha parseada es válida
      if (!isValid(fechaObj)) {
        console.warn('Fecha inválida:', fecha);
        return 'Fecha inválida';
      }
      
      // Formatear la fecha
      return format(fechaObj, formatoDeseado);
    } catch (error) {
      console.warn('Error al formatear fecha:', error, fecha);
      return 'Error de formato';
    }
  };
  
  // Preparar datos para el gráfico cuando se selecciona un cliente
  useEffect(() => {
    if (!clienteSeleccionado) {
      setDatosGrafico(null);
      return;
    }

    try {
      console.log("Preparando datos para el gráfico del cliente:", clienteSeleccionado);
      const cliente = evolucionClientes.find(c => c.cliente === clienteSeleccionado);
      if (!cliente) {
        console.error("Cliente no encontrado en datos");
        return;
      }

      console.log("Datos del cliente seleccionado:", cliente);
      console.log("Avisos actuales disponibles:", cliente.detalleActual?.length || 0);
      console.log("Avisos anteriores disponibles:", cliente.detalleAnterior?.length || 0);
      
      // Verificar que los totales coincidan
      let sumaDetalleActual = 0;
      cliente.detalleActual.forEach(aviso => {
        sumaDetalleActual += parseFloat(aviso.valor) || 0;
      });
      
      let sumaDetalleAnterior = 0;
      cliente.detalleAnterior.forEach(aviso => {
        sumaDetalleAnterior += parseFloat(aviso.valor) || 0;
      });
      
      console.log("Suma detalle actual:", sumaDetalleActual, "Total reportado:", cliente.inversionActual);
      console.log("Suma detalle anterior:", sumaDetalleAnterior, "Total reportado:", cliente.inversionAnterior);
      
      if (Math.abs(sumaDetalleActual - cliente.inversionActual) > 1) {
        console.warn("⚠️ Discrepancia en los totales actuales");
      }
      if (Math.abs(sumaDetalleAnterior - cliente.inversionAnterior) > 1) {
        console.warn("⚠️ Discrepancia en los totales anteriores");
      }

      // NUEVA IMPLEMENTACIÓN: Mostrar todos los avisos de forma absoluta en el tiempo
      const procesarAvisos = (avisos, esPeriodoActual = true, etiquetaPeriodo = "") => {
        console.log(`Procesando ${avisos?.length || 0} avisos para ${etiquetaPeriodo}`);
        
        if (!avisos || !Array.isArray(avisos) || avisos.length === 0) {
          console.warn(`No hay avisos para procesar o no es un array para ${etiquetaPeriodo}`);
          return [];
        }

        // Log de los primeros avisos para debug
        console.log(`Muestra de los primeros avisos de ${etiquetaPeriodo}:`, 
                   avisos.slice(0, 3).map(a => ({ 
                     fecha: a.fecha, 
                     valor: a.valor,
                     nid: a.nid
                   })));

        const resultado = [];
        let avisosConProblemas = 0;
        
        for (const aviso of avisos) {
          try {
            // Verificar datos básicos
            if (!aviso) {
              avisosConProblemas++;
              continue;
            }
            
            const fecha = aviso.fecha;
            const valor = parseFloat(aviso.valor) || 0;
            
            // Todos los avisos deben tener fecha
            if (!fecha) {
              console.warn("Aviso sin fecha:", aviso);
              avisosConProblemas++;
              continue;
            }
            
            // Convertir y validar fecha
            const fechaObj = new Date(fecha);
            if (isNaN(fechaObj.getTime())) {
              console.warn("Fecha inválida en aviso:", fecha, aviso);
              avisosConProblemas++;
              continue;
            }
            
            // Formatear la fecha de manera segura
            const fechaFormateada = formatearFechaSegura(fechaObj);
            
            resultado.push({
              x: fechaObj, // Usamos la fecha real como X
              y: valor,
              nid: aviso.nid || `sin-id-${Math.random().toString(36).substr(2, 9)}`,
              titulo: aviso.titulo || "Aviso sin título",
              fechaFormateada: fechaFormateada,
              valor: valor,
              esActual: esPeriodoActual,
              periodoNombre: etiquetaPeriodo
            });
          } catch (err) {
            console.warn("Error procesando aviso:", err, aviso);
            avisosConProblemas++;
          }
        }

        console.log(`Avisos con problemas en ${etiquetaPeriodo}: ${avisosConProblemas}`);
        console.log(`Avisos procesados correctamente para ${etiquetaPeriodo}: ${resultado.length}`);

        // Verificar y reportar la suma total de los avisos procesados
        const sumaProcesada = resultado.reduce((sum, aviso) => sum + aviso.valor, 0);
        console.log(`Suma de avisos ${etiquetaPeriodo} procesados:`, sumaProcesada);
        
        // Ordenar por fecha
        return resultado.sort((a, b) => a.x - b.x);
      };

      // Procesar todos los avisos
      const avisosActuales = procesarAvisos(cliente.detalleActual, true, "Período Actual");
      const avisosAnteriores = procesarAvisos(cliente.detalleAnterior, false, "Período Anterior");

      // Si no hay datos, mostrar mensaje
      if (avisosActuales.length === 0 && avisosAnteriores.length === 0) {
        console.warn("No hay datos de avisos para mostrar en el gráfico");
        setError("No hay datos de avisos para este cliente en los períodos seleccionados");
        return;
      }

      // Calcular totales para verificación
      const totalActualGrafico = avisosActuales.reduce((sum, a) => sum + a.valor, 0);
      const totalAnteriorGrafico = avisosAnteriores.reduce((sum, a) => sum + a.valor, 0);
      
      console.log("Total actual en gráfico:", totalActualGrafico, "vs Total reportado:", cliente.inversionActual);
      console.log("Total anterior en gráfico:", totalAnteriorGrafico, "vs Total reportado:", cliente.inversionAnterior);

      // Formatear las fechas de forma segura para las etiquetas
      const periodoActualFormateado = `${formatearFechaSegura(new Date(periodoActualInicio))} - ${formatearFechaSegura(new Date(periodoActualFin))}`;
      const periodoAnteriorFormateado = `${formatearFechaSegura(new Date(periodoComparacionInicio))} - ${formatearFechaSegura(new Date(periodoComparacionFin))}`;

      // Determinar la unidad de tiempo para mostrar adecuadamente los datos
      let tiempoUnidad = 'day';
      
      // Calcular el rango de días total
      const fechaInicioActual = new Date(periodoActualInicio);
      const fechaFinActual = new Date(periodoActualFin);
      
      if (isValid(fechaInicioActual) && isValid(fechaFinActual)) {
        const diasEnRango = Math.floor((fechaFinActual - fechaInicioActual) / (1000 * 60 * 60 * 24));
        
        if (diasEnRango > 60) {
          tiempoUnidad = 'month'; // Si es más de 60 días, mostrar por mes
        } else if (diasEnRango > 14) {
          tiempoUnidad = 'week'; // Si es más de 14 días, mostrar por semana
        }
        
        console.log(`Rango de ${diasEnRango} días, usando unidad de tiempo: ${tiempoUnidad}`);
      }

      // 4. Crear configuración del gráfico
      const datosGrafico = {
        datasets: [
          {
            label: `Período Actual (${periodoActualFormateado}) - Total: ${formatearMoneda(totalActualGrafico)}`,
            data: avisosActuales,
            backgroundColor: 'rgba(54, 162, 235, 0.8)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1,
            pointRadius: 6,
            pointHoverRadius: 8,
            showLine: false, // No mostrar línea para ver mejor los puntos individuales
            fill: false
          },
          {
            label: `Período Anterior (${periodoAnteriorFormateado}) - Total: ${formatearMoneda(totalAnteriorGrafico)}`,
            data: avisosAnteriores,
            backgroundColor: 'rgba(255, 99, 132, 0.8)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 1,
            pointRadius: 6, 
            pointHoverRadius: 8,
            borderDash: [5, 5],
            showLine: false, // No mostrar línea para ver mejor los puntos individuales
            fill: false
          }
        ]
      };

      // 5. Opciones del gráfico - Usando el eje X con tiempo
      const opcionesGraficoMejoradas = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: {
              unit: tiempoUnidad,
              displayFormats: {
                day: 'dd/MM',
                week: 'dd/MM',
                month: 'MM/yyyy'
              },
              tooltipFormat: 'dd/MM/yyyy'
            },
            title: {
              display: true,
              text: 'Fecha de publicación'
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45
            }
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Valor ($)'
            },
            ticks: {
              callback: function(value) {
                return formatearMoneda(value);
              }
            }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: function(tooltipItems) {
                // Mostrar información sobre la fecha
                const item = tooltipItems[0];
                const aviso = item.raw;
                const periodoTexto = aviso.periodoNombre || (aviso.esActual 
                  ? `Período Actual`
                  : `Período Anterior`);
                
                return `${periodoTexto} - ${aviso.fechaFormateada}`;
              },
              label: function(tooltipItem) {
                // Mostrar información detallada del aviso
                const aviso = tooltipItem.raw;
                return [
                  `Valor: ${formatearMoneda(aviso.valor)}`,
                  `Título: ${aviso.titulo || "Sin título"}`,
                  `ID: ${aviso.nid || "No disponible"}`
                ];
              }
            }
          },
          legend: {
            position: 'top',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle'
            }
          },
          title: {
            display: true,
            text: `Avisos de: ${clienteSeleccionado}`,
            font: {
              size: 16
            }
          }
        },
        interaction: {
          mode: 'nearest',
          intersect: true
        }
      };

      console.log("Configuración del gráfico preparada. Mostrando datos:", {
        avisosActuales: avisosActuales.length,
        avisosAnteriores: avisosAnteriores.length,
        totalActual: totalActualGrafico,
        totalAnterior: totalAnteriorGrafico,
        unidadTiempo: tiempoUnidad
      });
      
      setDatosGrafico(datosGrafico);
      setOpcionesGrafico(opcionesGraficoMejoradas);
    } catch (error) {
      console.error("Error al preparar datos del gráfico:", error);
      setError(`Error al preparar gráfico: ${error.message}`);
    }
  }, [clienteSeleccionado, evolucionClientes, formatearMoneda, periodoActualInicio, periodoActualFin, periodoComparacionInicio, periodoComparacionFin]);
  
  // Estado para las opciones del gráfico
  const [opcionesGrafico, setOpcionesGrafico] = useState({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'day',
          displayFormats: {
            day: 'dd/MM/yyyy'
          },
          tooltipFormat: 'dd/MM/yyyy'
        },
        title: {
          display: true,
          text: 'Fecha de publicación'
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Valor ($)'
        },
        ticks: {
          callback: function(value) {
            return formatearMoneda(value);
          }
        }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatearMoneda(context.parsed.y);
            }
            return label;
          }
        }
      },
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Evolución de inversión por período',
        font: {
          size: 16
        }
      }
    }
  });
  
  // Calcular totales para los cards
  const calcularTotales = () => {
    const totalActual = evolucionClientes.reduce((sum, cliente) => sum + cliente.inversionActual, 0);
    const totalAnterior = evolucionClientes.reduce((sum, cliente) => sum + cliente.inversionAnterior, 0);
    const variacion = totalActual - totalAnterior;
    const porcentaje = totalAnterior !== 0 ? (variacion / totalAnterior) * 100 : 0;
    
    const clientesPerdidos = evolucionClientes.filter(c => c.estado === 'perdidos').length;
    const clientesNuevos = evolucionClientes.filter(c => c.estado === 'nuevos').length;
    const clientesActivos = evolucionClientes.filter(c => c.estado === 'activo').length;
    
    return {
      totalActual,
      totalAnterior,
      variacion,
      porcentaje,
      clientesPerdidos,
      clientesNuevos,
      clientesActivos,
      totalClientes: evolucionClientes.length
    };
  };
  
  const totales = calcularTotales();
  
  // Manejar cambio de tipo de periodo
  const handleTipoPeriodoChange = (tipo) => {
    setTipoPeriodo(tipo);
    
    const hoy = new Date();
    let fechaInicio, fechaFin;
    
    switch (tipo) {
      case 'mes-actual':
        fechaInicio = startOfMonth(hoy);
        fechaFin = endOfMonth(hoy);
        break;
        
      case 'ultimo-mes':
        fechaInicio = startOfMonth(subMonths(hoy, 1));
        fechaFin = endOfMonth(subMonths(hoy, 1));
        break;
      
      case 'ultimo-trimestre':
        fechaInicio = startOfMonth(subMonths(hoy, 3));
        fechaFin = endOfMonth(subDays(hoy, 1));
        break;
      
      case 'ultimo-semestre':
        fechaInicio = startOfMonth(subMonths(hoy, 6));
        fechaFin = endOfMonth(subDays(hoy, 1));
        break;
      
      case 'ultimo-anio':
        fechaInicio = startOfMonth(subMonths(hoy, 12));
        fechaFin = endOfMonth(subDays(hoy, 1));
        break;
      
      case 'anio-actual':
        fechaInicio = startOfYear(hoy);
        fechaFin = endOfMonth(hoy);  // Actualizado para usar hasta fin del mes actual
        break;
      
      case 'personalizado':
        // Mantener las fechas actuales para personalizado
        return;
      
      default:
        return;
    }
    
    setPeriodoActualInicio(format(fechaInicio, 'yyyy-MM-dd'));
    setPeriodoActualFin(format(fechaFin, 'yyyy-MM-dd'));
  };
  
  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-3">Evolución de Clientes</h1>
          <p className="text-muted">
            Analiza la evolución de la inversión de clientes y detecta clientes perdidos o nuevos
          </p>
        </Col>
      </Row>
      
      {/* Filtros de periodo */}
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={(e) => {
            e.preventDefault();
            cargarDatos(); // Esta función se llamará solo cuando se presione el botón
          }}>
            {/* Selector de tipo de periodo */}
            <Row className="mb-3">
              <Col>
                <Form.Label>Seleccionar periodo de análisis:</Form.Label>
                <div>
                  <ButtonGroup className="w-100">
                    <ToggleButton
                      id="periodo-mes-actual"
                      type="radio"
                      variant="outline-primary"
                      name="periodo"
                      value="mes-actual"
                      checked={tipoPeriodo === 'mes-actual'}
                      onChange={() => handleTipoPeriodoChange('mes-actual')}
                    >
                      Mes actual
                    </ToggleButton>
                    <ToggleButton
                      id="periodo-ultimo-mes"
                      type="radio"
                      variant="outline-primary"
                      name="periodo"
                      value="ultimo-mes"
                      checked={tipoPeriodo === 'ultimo-mes'}
                      onChange={() => handleTipoPeriodoChange('ultimo-mes')}
                    >
                      Último mes
                    </ToggleButton>
                    <ToggleButton
                      id="periodo-ultimo-trimestre"
                      type="radio"
                      variant="outline-primary"
                      name="periodo"
                      value="ultimo-trimestre"
                      checked={tipoPeriodo === 'ultimo-trimestre'}
                      onChange={() => handleTipoPeriodoChange('ultimo-trimestre')}
                    >
                      Último trimestre
                    </ToggleButton>
                    <ToggleButton
                      id="periodo-ultimo-semestre"
                      type="radio"
                      variant="outline-primary"
                      name="periodo"
                      value="ultimo-semestre"
                      checked={tipoPeriodo === 'ultimo-semestre'}
                      onChange={() => handleTipoPeriodoChange('ultimo-semestre')}
                    >
                      Último semestre
                    </ToggleButton>
                    <ToggleButton
                      id="periodo-ultimo-anio"
                      type="radio"
                      variant="outline-primary"
                      name="periodo"
                      value="ultimo-anio"
                      checked={tipoPeriodo === 'ultimo-anio'}
                      onChange={() => handleTipoPeriodoChange('ultimo-anio')}
                    >
                      Último año
                    </ToggleButton>
                    <ToggleButton
                      id="periodo-anio-actual"
                      type="radio"
                      variant="outline-primary"
                      name="periodo"
                      value="anio-actual"
                      checked={tipoPeriodo === 'anio-actual'}
                      onChange={() => handleTipoPeriodoChange('anio-actual')}
                    >
                      Año actual
                    </ToggleButton>
                    <ToggleButton
                      id="periodo-personalizado"
                      type="radio"
                      variant="outline-primary"
                      name="periodo"
                      value="personalizado"
                      checked={tipoPeriodo === 'personalizado'}
                      onChange={() => handleTipoPeriodoChange('personalizado')}
                    >
                      Personalizado
                    </ToggleButton>
                  </ButtonGroup>
                </div>
              </Col>
            </Row>
            
            {/* Fechas (siempre visibles, pero solo editables cuando es personalizado) */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Periodo Actual - Desde</Form.Label>
                  <Form.Control
                    type="date"
                    value={periodoActualInicio}
                    onChange={(e) => {
                      setPeriodoActualInicio(e.target.value);
                      setTipoPeriodo('personalizado');
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Periodo Actual - Hasta</Form.Label>
                  <Form.Control
                    type="date"
                    value={periodoActualFin}
                    onChange={(e) => {
                      setPeriodoActualFin(e.target.value);
                      setTipoPeriodo('personalizado');
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Periodo Comparación (Año Anterior) - Desde</Form.Label>
                  <Form.Control
                    type="date"
                    value={periodoComparacionInicio}
                    readOnly
                    disabled
                    className="bg-light"
                  />
                  <Form.Text className="text-muted">
                    El periodo de comparación siempre es el mismo periodo del año anterior.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Periodo Comparación (Año Anterior) - Hasta</Form.Label>
                  <Form.Control
                    type="date"
                    value={periodoComparacionFin}
                    readOnly
                    disabled
                    className="bg-light"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Filtrar por Medio</Form.Label>
                  <Form.Select
                    value={medioSeleccionado}
                    onChange={(e) => setMedioSeleccionado(e.target.value)}
                  >
                    <option value="">Todos los medios</option>
                    {mediosDisponibles.map((medio) => (
                      <option key={medio.id} value={medio.id}>{medio.nombre}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Comparación</Form.Label>
                  <Form.Select
                    value={tipoComparacion}
                    onChange={(e) => setTipoComparacion(e.target.value)}
                  >
                    <option value="anio_anterior">Mismo período año anterior</option>
                    <option value="periodo_atras">Mismo período hacia atrás</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={8}>
                <Button variant="primary" type="submit" className="mb-3">
                  <i className="bi bi-search me-1"></i> Aplicar filtros
                </Button>
              </Col>
              <Col md={4}>
                {/* Búsqueda de cliente específico */}
                <Form.Group className="mb-3 position-relative">
                  <Form.Label>Buscar cliente específico</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Nombre del cliente..."
                    value={busquedaCliente}
                    onChange={(e) => setBusquedaCliente(e.target.value)}
                  />
                  {clientesFiltradosBusqueda.length > 0 && (
                    <div 
                      className="position-absolute w-100 bg-white border rounded shadow-sm"
                      style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                    >
                      {clientesFiltradosBusqueda.map((cliente, idx) => (
                        <div 
                          key={idx} 
                          className="p-2 border-bottom cursor-pointer hover-bg-light"
                          onClick={() => handleSeleccionarCliente(cliente.cliente)}
                          style={{ cursor: 'pointer' }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          {cliente.cliente}
                          <div className="small text-muted">
                            Inversión actual: {formatearMoneda(cliente.inversionActual)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
      
      {/* Mostrar error si existe */}
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      
      {/* Tarjetas de resumen */}
      {!isLoading && evolucionClientes.length > 0 && (
        <Row className="mb-4">
          <Col md={6} lg={3}>
            <Card className="mb-3 bg-primary text-white">
              <Card.Body>
                <h5 className="card-title">Inversión Actual</h5>
                <h2 className="card-text">{formatearMoneda(totales.totalActual)}</h2>
                <p className="mb-0">
                  vs. {formatearMoneda(totales.totalAnterior)} ({formatearPorcentaje(totales.porcentaje)})
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={3}>
            <Card className="mb-3 bg-danger text-white">
              <Card.Body>
                <h5 className="card-title">Clientes Perdidos</h5>
                <h2 className="card-text">{totales.clientesPerdidos}</h2>
                <p className="mb-0">
                  de {totales.totalClientes} clientes ({((totales.clientesPerdidos / totales.totalClientes) * 100).toFixed(1)}%)
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={3}>
            <Card className="mb-3 bg-success text-white">
              <Card.Body>
                <h5 className="card-title">Clientes Nuevos</h5>
                <h2 className="card-text">{totales.clientesNuevos}</h2>
                <p className="mb-0">
                  de {totales.totalClientes} clientes ({((totales.clientesNuevos / totales.totalClientes) * 100).toFixed(1)}%)
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={3}>
            <Card className="mb-3 bg-info text-white">
              <Card.Body>
                <h5 className="card-title">Clientes Activos</h5>
                <h2 className="card-text">{totales.clientesActivos}</h2>
                <p className="mb-0">
                  de {totales.totalClientes} clientes ({((totales.clientesActivos / totales.totalClientes) * 100).toFixed(1)}%)
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      
      {/* Modal para mostrar información del cliente */}
      <Modal 
        show={showModal && clienteSeleccionado} 
        onHide={() => setShowModal(false)} 
        size="xl"
        backdrop="static"
        keyboard={false}
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex justify-content-between align-items-center w-100">
              <span>Avisos de: {clienteSeleccionado}</span>
              <div>
                <Badge bg="primary" className="me-2">
                  Total actual: {formatearMoneda(evolucionClientes.find(c => c.cliente === clienteSeleccionado)?.inversionActual || 0)}
                </Badge>
                <Badge bg="secondary" className="me-2">
                  Total anterior: {formatearMoneda(evolucionClientes.find(c => c.cliente === clienteSeleccionado)?.inversionAnterior || 0)}
                </Badge>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error ? (
            <Alert variant="warning">{error}</Alert>
          ) : !datosGrafico ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-2">Preparando visualización de datos...</p>
            </div>
          ) : (
            <div style={{ height: '400px' }}>
              <Line data={datosGrafico} options={opcionesGrafico} />
            </div>
          )}
          
          {/* Información adicional del cliente */}
          {evolucionClientes.find(c => c.cliente === clienteSeleccionado) && (
            <div className="mt-3">
              <h6>Resumen:</h6>
              <Row>
                <Col md={3}>
                  <div className="mb-2">
                    <strong>Inversión actual:</strong> {formatearMoneda(evolucionClientes.find(c => c.cliente === clienteSeleccionado).inversionActual)}
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-2">
                    <strong>Inversión año anterior:</strong> {formatearMoneda(evolucionClientes.find(c => c.cliente === clienteSeleccionado).inversionAnterior)}
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-2">
                    <strong>Variación:</strong> {formatearPorcentaje(evolucionClientes.find(c => c.cliente === clienteSeleccionado).variacionPorcentaje)}
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-2">
                    <strong>Estado:</strong> {' '}
                    {evolucionClientes.find(c => c.cliente === clienteSeleccionado).estado === 'perdidos' && <span className="badge bg-danger">Perdido</span>}
                    {evolucionClientes.find(c => c.cliente === clienteSeleccionado).estado === 'nuevos' && <span className="badge bg-success">Nuevo</span>}
                    {evolucionClientes.find(c => c.cliente === clienteSeleccionado).estado === 'activo' && <span className="badge bg-info">Activo</span>}
                  </div>
                </Col>
              </Row>
              <hr />
              <h6>Detalle de avisos:</h6>
              <Row>
                <Col md={6}>
                  <div>
                    <strong>Periodo Actual ({formatearFechaSegura(new Date(periodoActualInicio))} - {formatearFechaSegura(new Date(periodoActualFin))}):</strong>
                    <div className="alert alert-info mb-2">
                      Total avisos: {evolucionClientes.find(c => c.cliente === clienteSeleccionado).detalleActual.length || 0} 
                      &nbsp;|&nbsp; Total inversión: {formatearMoneda(evolucionClientes.find(c => c.cliente === clienteSeleccionado).inversionActual)}
                    </div>
                    <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <Table size="sm" className="mt-2" striped bordered hover>
                        <thead>
                          <tr>
                            <th>Fecha</th>
                            <th>Título</th>
                            <th className="text-end">Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {evolucionClientes.find(c => c.cliente === clienteSeleccionado).detalleActual
                            .sort((a, b) => {
                              try {
                                return new Date(a.fecha) - new Date(b.fecha);
                              } catch (e) {
                                return 0;
                              }
                            })
                            .map((aviso, idx) => (
                              <tr key={`actual-${aviso.nid || idx}`}>
                                <td>{formatearFechaSegura(aviso.fecha)}</td>
                                <td>{aviso.titulo || 'Sin título'}</td>
                                <td className="text-end">{formatearMoneda(aviso.valor)}</td>
                              </tr>
                            ))
                          }
                          <tr className="table-primary">
                            <td colSpan={2} className="text-end"><strong>Total:</strong></td>
                            <td className="text-end"><strong>{formatearMoneda(
                              evolucionClientes.find(c => c.cliente === clienteSeleccionado).detalleActual
                                .reduce((sum, aviso) => sum + parseFloat(aviso.valor || 0), 0)
                            )}</strong></td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div>
                    <strong>Periodo Anterior ({formatearFechaSegura(new Date(periodoComparacionInicio))} - {formatearFechaSegura(new Date(periodoComparacionFin))}):</strong>
                    <div className="alert alert-secondary mb-2">
                      Total avisos: {evolucionClientes.find(c => c.cliente === clienteSeleccionado).detalleAnterior.length || 0}
                      &nbsp;|&nbsp; Total inversión: {formatearMoneda(evolucionClientes.find(c => c.cliente === clienteSeleccionado).inversionAnterior)}
                    </div>
                    <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <Table size="sm" className="mt-2" striped bordered hover>
                        <thead>
                          <tr>
                            <th>Fecha</th>
                            <th>Título</th>
                            <th className="text-end">Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {evolucionClientes.find(c => c.cliente === clienteSeleccionado).detalleAnterior
                            .sort((a, b) => {
                              try {
                                return new Date(a.fecha) - new Date(b.fecha);
                              } catch (e) {
                                return 0;
                              }
                            })
                            .map((aviso, idx) => (
                              <tr key={`anterior-${aviso.nid || idx}`}>
                                <td>{formatearFechaSegura(aviso.fecha)}</td>
                                <td>{aviso.titulo || 'Sin título'}</td>
                                <td className="text-end">{formatearMoneda(aviso.valor)}</td>
                              </tr>
                            ))
                          }
                          <tr className="table-secondary">
                            <td colSpan={2} className="text-end"><strong>Total:</strong></td>
                            <td className="text-end"><strong>{formatearMoneda(
                              evolucionClientes.find(c => c.cliente === clienteSeleccionado).detalleAnterior
                                .reduce((sum, aviso) => sum + parseFloat(aviso.valor || 0), 0)
                            )}</strong></td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowModal(false);
            // Opcional: deseleccionar cliente al cerrar
            // setClienteSeleccionado(null);
          }}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Tabs de análisis */}
      <Card>
        <Card.Body>
          <Tabs defaultActiveKey="todos" id="clientes-tabs" className="mb-3" onSelect={(k) => setClientePerdidosFiltro(k)}>
            <Tab eventKey="todos" title={`Todos (${evolucionClientes.length})`}>
              <TablaClientes 
                clientes={clientesFiltrados} 
                isLoading={isLoading}
                formatearMoneda={formatearMoneda}
                formatearPorcentaje={formatearPorcentaje}
                onClienteSeleccionado={handleSeleccionarCliente}
                clienteSeleccionado={clienteSeleccionado}
              />
            </Tab>
            <Tab eventKey="perdidos" title={`Perdidos (${totales.clientesPerdidos})`}>
              <TablaClientes 
                clientes={clientesFiltrados} 
                isLoading={isLoading}
                formatearMoneda={formatearMoneda}
                formatearPorcentaje={formatearPorcentaje}
                onClienteSeleccionado={handleSeleccionarCliente}
                clienteSeleccionado={clienteSeleccionado}
              />
            </Tab>
            <Tab eventKey="nuevos" title={`Nuevos (${totales.clientesNuevos})`}>
              <TablaClientes 
                clientes={clientesFiltrados} 
                isLoading={isLoading}
                formatearMoneda={formatearMoneda}
                formatearPorcentaje={formatearPorcentaje}
                onClienteSeleccionado={handleSeleccionarCliente}
                clienteSeleccionado={clienteSeleccionado}
              />
            </Tab>
            <Tab eventKey="activo" title={`Activos (${totales.clientesActivos})`}>
              <TablaClientes 
                clientes={clientesFiltrados} 
                isLoading={isLoading}
                formatearMoneda={formatearMoneda}
                formatearPorcentaje={formatearPorcentaje}
                onClienteSeleccionado={handleSeleccionarCliente}
                clienteSeleccionado={clienteSeleccionado}
              />
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </Container>
  );
};

// Componente para la tabla de clientes
const TablaClientes = ({ 
  clientes, 
  isLoading, 
  formatearMoneda, 
  formatearPorcentaje,
  onClienteSeleccionado,
  clienteSeleccionado
}) => {
  // Estados para ordenamiento
  const [ordenarPor, setOrdenarPor] = useState('variacionPorcentaje'); // Columna para ordenar
  const [ordenDireccion, setOrdenDireccion] = useState('desc'); // 'asc' o 'desc'
  
  // Función para cambiar ordenamiento
  const cambiarOrden = (columna) => {
    if (ordenarPor === columna) {
      // Si ya estamos ordenando por esta columna, invertir dirección
      setOrdenDireccion(ordenDireccion === 'asc' ? 'desc' : 'asc');
    } else {
      // Si es una columna nueva, establecerla como predeterminada y ordenar descendente
      setOrdenarPor(columna);
      setOrdenDireccion('desc');
    }
  };
  
  // Renderizar cabecera con indicador de ordenamiento
  const renderCabecera = (texto, columna, className = '') => (
    <th 
      className={className}
      onClick={() => cambiarOrden(columna)}
      style={{ cursor: 'pointer' }}
    >
      {texto} {' '}
      {ordenarPor === columna && (
        <i className={`bi bi-caret-${ordenDireccion === 'asc' ? 'up' : 'down'}-fill`}></i>
      )}
    </th>
  );
  
  // Ordenar clientes
  const clientesOrdenados = [...clientes].sort((a, b) => {
    let valorA = a[ordenarPor];
    let valorB = b[ordenarPor];
    
    // Manejo especial para campos numéricos que podrían estar formateados
    if (ordenarPor === 'inversionActual' || ordenarPor === 'inversionAnterior' || ordenarPor === 'variacionInversion') {
      valorA = parseFloat(valorA) || 0;
      valorB = parseFloat(valorB) || 0;
    }
    
    // Comparar según dirección de ordenamiento
    if (ordenDireccion === 'asc') {
      return valorA > valorB ? 1 : -1;
    } else {
      return valorA < valorB ? 1 : -1;
    }
  });
  
  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-3">Cargando datos...</p>
      </div>
    );
  }

  if (!clientes || clientes.length === 0) {
    return (
      <Alert variant="warning" className="text-center p-4">
        <Alert.Heading>No se encontraron resultados</Alert.Heading>
        <p>No hay clientes que cumplan con los criterios seleccionados.</p>
      </Alert>
    );
  }

  return (
    <div className="table-responsive">
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>#</th>
            {renderCabecera('Cliente', 'cliente')}
            {renderCabecera('Inversión Actual', 'inversionActual', 'text-end')}
            {renderCabecera('Inversión Anterior', 'inversionAnterior', 'text-end')}
            {renderCabecera('Variación ($)', 'variacionInversion', 'text-end')}
            {renderCabecera('Variación (%)', 'variacionPorcentaje', 'text-end')}
            {renderCabecera('Avisos Act.', 'avisosActual', 'text-end')}
            {renderCabecera('Avisos Ant.', 'avisosAnterior', 'text-end')}
            {renderCabecera('Estado', 'estado', 'text-center')}
            <th className="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {clientesOrdenados.map((cliente, index) => (
            <tr 
              key={index} 
              className={clienteSeleccionado === cliente.cliente ? 'table-primary' : ''}
            >
              <td>{index + 1}</td>
              <td>{cliente.cliente}</td>
              <td className="text-end">{formatearMoneda(cliente.inversionActual)}</td>
              <td className="text-end">{formatearMoneda(cliente.inversionAnterior)}</td>
              <td className={`text-end ${cliente.variacionInversion >= 0 ? 'text-success' : 'text-danger'}`}>
                {cliente.variacionInversion >= 0 ? '+' : ''}{formatearMoneda(cliente.variacionInversion)}
              </td>
              <td className="text-end">{formatearPorcentaje(cliente.variacionPorcentaje)}</td>
              <td className="text-end">{cliente.avisosActual}</td>
              <td className="text-end">{cliente.avisosAnterior}</td>
              <td className="text-center">
                {cliente.estado === 'perdidos' && <span className="badge bg-danger">Perdido</span>}
                {cliente.estado === 'nuevos' && <span className="badge bg-success">Nuevo</span>}
                {cliente.estado === 'activo' && <span className="badge bg-info">Activo</span>}
              </td>
              <td className="text-center">
                <Button 
                  variant={clienteSeleccionado === cliente.cliente ? "outline-primary" : "primary"} 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation(); // Evitar que el evento se propague a la fila
                    onClienteSeleccionado(clienteSeleccionado === cliente.cliente ? null : cliente.cliente);
                  }}
                >
                  {clienteSeleccionado === cliente.cliente ? "Ocultar" : "Ver evolución"}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default EvolucionClientes; 